/**
 * The local file cache (indexedDbFileStorage) keys a file by (project, scope, path inside the scope). A scope is the
 * folder a file tree is opened on: '.' for the project root (Home › Files), `services/app` for a service's code view,
 * `brain`… The same project file therefore has one cache entry per scope it was seen in — `services/app/src/a.js`
 * is ('.', 'services/app/src/a.js') AND ('services/app', 'src/a.js'). These helpers map between the two so a change
 * made through one scope reaches the others.
 */

export const isRootScope = (scope) => !scope || scope === '.' || scope === './';

const clean = (relPath) => String(relPath || '').split('/').filter((segment) => segment && segment !== '.').join('/');

/** Project-relative path of `filePath` inside `scope`. */
export function fullPathOf(scope, filePath) {
  return isRootScope(scope) ? clean(filePath) : [clean(scope), clean(filePath)].filter(Boolean).join('/');
}

/** `fullPath` relative to `scope`, or null when it lies outside it. */
export function relativeTo(scope, fullPath) {
  const full = clean(fullPath);
  if (isRootScope(scope)) { return full; }
  const prefix = `${clean(scope)}/`;
  return full.startsWith(prefix) ? full.slice(prefix.length) : null;
}

/**
 * Every scope that can hold a cache entry for `fullPath`: the root and each of its parent folders, with the path
 * inside each. `services/app/src/a.js` → '.', 'services', 'services/app', 'services/app/src'.
 */
export function scopesContaining(fullPath) {
  const segments = clean(fullPath).split('/').filter(Boolean);
  const scopes = [{ scope: '.', filePath: segments.join('/') }];
  for (let index = 1; index < segments.length; index += 1) {
    scopes.push({ scope: segments.slice(0, index).join('/'), filePath: segments.slice(index).join('/') });
  }
  return scopes;
}

/** True when two scopes see some of the same files (one contains the other). */
export function scopesOverlap(a, b) {
  if (isRootScope(a) || isRootScope(b)) { return true; }
  const left = clean(a);
  const right = clean(b);
  return left === right || left.startsWith(`${right}/`) || right.startsWith(`${left}/`);
}
